import { Sensor, useHvacFilterStatus } from 'common';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { YStack, XStack, Text } from 'tamagui';
import { tokens } from 'tamagui-config';

import { LoadingOverlay } from '../../../components/common';
import { CircularProgress } from '../../../components/common/CircularProgress';

/**
 * Maps filter threshold category to a color
 */
const mapFilterThresholdToFillColor = (thresholdCategory: string): string => {
  const map: Record<string, string> = {
    good: tokens.color.successGreen,
    warning: tokens.color.warning,
    alert: tokens.color.energyRed,
    poor: tokens.color.energyRed,
  };
  return map[thresholdCategory] || tokens.color.successGreen;
};

interface AirFilterStatusContentProps {
  sensor?: Sensor;
}

export const AirFilterStatusContent: React.FC<AirFilterStatusContentProps> = ({ sensor }) => {
  const { t } = useTranslation();
  const { filterStatus, loading, error } = useHvacFilterStatus(sensor?.id);
  
  // Define format functions outside of conditional blocks
  const formatReplacementTime = (replacementTime: string): string => {
    switch (replacementTime) {
      case 'now':
        return t('airFilter.replaceNow');
      case 'soon':
        return t('airFilter.replaceSoon');
      case 'inOneMonth':
        return t('airFilter.replaceInOneMonth');
      case 'inThreeMonths':
        return t('airFilter.replaceInThreeMonths');
      case 'inSixMonths':
        return t('airFilter.replaceInSixMonths');
      default:
        return t('airFilter.replacementNotNeeded');
    }
  };

  // Create a display data object if we have filter status
  const displayData = useMemo(() => {
    if (!filterStatus) return null;
    
    const value = filterStatus.filterHealthPercentage || 0;
    const rawValue = filterStatus.pressureDelta || 0;
    const label = `${t('common.main')} - ${rawValue.toFixed(3)} inH2O`;
    const color = mapFilterThresholdToFillColor(filterStatus.thresholdCategory);
    const timestamp = new Date(filterStatus.calculatedAt).toLocaleString();
    
    return { value, rawValue, label, color, timestamp };
  }, [filterStatus, t]);
  
  if (loading) {
    return (
      <YStack flex={1} justifyContent="center" alignItems="center" padding={'$4.5'}>
        <LoadingOverlay isVisible={true} />
      </YStack>
    );
  }

  if (error || !filterStatus || !displayData) {
    return (
      <YStack flex={1} justifyContent="center" alignItems="center" padding={'$4.5'}>
        <Text fontSize="$6" color={tokens.color.textLight} textAlign="center">
          {error ? t('common.errorLoadingData') : t('common.noData')}
        </Text>
      </YStack>
    );
  }

  return (
    <YStack padding={'$4.5'} justifyContent="center" alignItems="center" space="$3">
      <CircularProgress
        value={displayData.value}
        label={displayData.label}
        progressColor={displayData.color}
      />
      
      <YStack space="$2" width="100%" marginTop="$3">
        <XStack justifyContent="space-between">
          <Text color={tokens.color.textLight} fontSize="$3">{t('airFilter.status')}:</Text>
          <Text fontSize="$3" fontWeight="500">{filterStatus.status}</Text>
        </XStack>
        
        <XStack justifyContent="space-between">
          <Text color={tokens.color.textLight} fontSize="$3">{t('airFilter.replacementTime')}:</Text>
          <Text fontSize="$3" fontWeight="500">
            {formatReplacementTime(filterStatus.replacementTime)}
          </Text>
        </XStack>
        
        <XStack justifyContent="space-between">
          <Text color={tokens.color.textLight} fontSize="$3">{t('common.lastUpdated')}:</Text>
          <Text fontSize="$3">{displayData.timestamp}</Text>
        </XStack>
      </YStack>
    </YStack>
  );
};
